// pages/habilitations/ImportExcelModal.js
import React, { useState } from 'react';
import {
  Upload,
  Button,
  Alert,
  Table,
  Typography,
  Space,
  Result,
} from 'antd';
import {
    InboxOutlined, FileExcelOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import {habilitationsService} from "../../services/HabilitationsService";
import CBModal from '../../components/CBModal';

import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../i18n/libelles';

const { Dragger } = Upload;
const { Text } = Typography;

const ERROR_COLUMNS = avecLibelleTraduit([
    {
        dataIndex: 'ligne',
        width: 70,
        render: (v) => <Text type="danger" strong>#{v}</Text>,
    },
    {
        dataIndex: 'champ',
        width: 160,
        render: (v) => <Text code>{v}</Text>,
    },
    {
        dataIndex: 'raison',
    },
], 'habilitations:import.colonnes', { cle: 'dataIndex', champ: 'title' });

export default function ImportExcelModal({ open, onClose, onSuccess }) {
    const { t } = useTranslation('habilitations');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [imported, setImported] = useState(null); // nombre créés

    const reset = () => {
        setFile(null);
        setErrors([]);
        setImported(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleImport = async () => {
        if (!file) return;
        setLoading(true);
        setErrors([]);
        setImported(null);
        try {
            const res = await habilitationsService.import(file);
            const data = res.data;
            if (data.erreurs && data.erreurs.length > 0) {
                setErrors(data.erreurs);
            } else {
                setImported(data.totalCrees);
                onSuccess(data.totalCrees);
            }
        } catch (err) {
            const data = err?.response?.data;
            if (data?.erreurs?.length) {
                setErrors(data.erreurs);
            } else {
                setErrors([{ ligne: '-', champ: 'format', raison: data?.message || t('import.erreurInconnue') }]);
            }
        } finally {
            setLoading(false);
        }
    };

    const uploadProps = {
        name: 'file',
        accept: '.xlsx',
        multiple: false,
        maxCount: 1,
        beforeUpload: (f) => {
            setFile(f);
            setErrors([]);
            setImported(null);
            return false; // empêche l'upload auto
        },
        onRemove: () => {
            setFile(null);
            setErrors([]);
        },
        fileList: file ? [file] : [],
    };

    return (
        <CBModal
            title={
                <Space>
                    <FileExcelOutlined style={{ color: '#1a6b3c' }} />
                    {t('import.titre')}
                </Space>
            }
            open={open}
            onCancel={handleClose}
            width={700}
            footer={
                imported !== null ? (
                    <Button type="primary" onClick={handleClose}>{t('import.fermer')}</Button>
                ) : (
                    <Space>
                        <Button onClick={handleClose}>{t('import.annuler')}</Button>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={handleImport}
                            disabled={!file}
                            loading={loading}
                        >
                            {t('import.importer')}
                        </Button>
                    </Space>
                )
            }
            destroyOnClose
        >
            {imported !== null ? (
                <Result
                    icon={<CheckCircleOutlined style={{ color: '#1a6b3c' }} />}
                    status="success"
                    title={t('import.succes', { count: imported })}
                />
            ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Dragger {...uploadProps} style={{ borderColor: errors.length ? '#ff4d4f' : undefined }}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined style={{ color: '#1a6b3c' }} />
                        </p>
                        <p className="ant-upload-text">{t('import.deposer')}</p>
                        {/* LA COLONNE « codeHabilitation » A DISPARU DE CETTE LISTE comme
                            elle a disparu du fichier : le code est compose par le serveur
                            depuis le module et l'action. L'annoncer encore ici aurait fait
                            preparer une colonne que le controle d'en-tetes rejette, et
                            l'import aurait echoue sur la foi de cette phrase. */}
                        <p className="ant-upload-hint">
                            {t('import.colonnesAttendues')}
                            <Text code style={{ marginLeft: 4 }}>
                                libelleHabilitation · description · action · typeAcces · nomModule
                            </Text>
                        </p>
                        <p className="ant-upload-hint" style={{ marginTop: 4 }}>
                            {t('import.codeAutomatique')}
                        </p>
                    </Dragger>

                    <Alert
                        type="info"
                        showIcon
                        message={t('import.transactionnel')}
                    />

                    {errors.length > 0 && (
                        <div>
                            <Alert
                                type="error"
                                showIcon
                                message={t('import.erreursDetectees', { count: errors.length })}
                                style={{ marginBottom: 12 }}
                            />
                            <Table
                                dataSource={errors.map((e, i) => ({ ...e, key: i }))}
                                columns={ERROR_COLUMNS}
                                size="small"
                                pagination={errors.length > 10 ? { pageSize: 10 } : false}
                                scroll={{ y: 240 }}
                            />
                        </div>
                    )}
                </Space>
            )}
        </CBModal>
    );
}